import { countPips, type PipCountingOptions } from "./pipCounting";
import { darknessScore, toPipScore } from "./pipScore";
import { detectTiles, type TileDetectionOptions } from "./tileDetection";
import { toWhitenessMap } from "./whiteness";
import type { Domino, DetectionResult, GrayImage } from "./types";

export interface DetectOptions {
  tiles?: TileDetectionOptions;
  pips?: PipCountingOptions;
}

/**
 * Core detection: given a tile map (a luminance or whiteness map, for finding
 * tiles) and a pip-score map (for counting coloured pips), find every tile and
 * total the pips.
 */
function detectWithMaps(
  tileMap: GrayImage,
  pipScore: GrayImage,
  options: DetectOptions,
): DetectionResult {
  const tiles = detectTiles(tileMap, options.tiles);

  const dominoes: Domino[] = tiles.map((tile) => {
    const halves = countPips(pipScore, tile, options.pips);
    const values: [number, number] = [halves[0].pips, halves[1].pips];
    return {
      values,
      halves,
      total: values[0] + values[1],
      bbox: tile.bbox,
      orientation: tile.orientation,
    };
  });

  const totalPips = dominoes.reduce((sum, d) => sum + d.total, 0);
  return { dominoes, totalPips };
}

/**
 * Run the full detection pipeline on a grayscale image. Without colour
 * information, pips are detected purely by darkness (dark pips on light tiles).
 * Prefer {@link detectDominoesFromRGBA} for real, colour-coded photos.
 */
export function detectDominoes(
  image: GrayImage,
  options: DetectOptions = {},
): DetectionResult {
  return detectWithMaps(image, darknessScore(image), options);
}

/**
 * Run the full detection pipeline on raw RGBA pixels (as returned by a canvas's
 * `getImageData().data`). Uses colour on both sides of the pipeline: a whiteness
 * map segments the neutral white tiles from a coloured surface, and a pip-score
 * map detects pips of any hue — so it handles colour-coded domino sets on real
 * backgrounds (e.g. wood).
 */
export function detectDominoesFromRGBA(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  options: DetectOptions = {},
): DetectionResult {
  const tileMap = toWhitenessMap(rgba, width, height);
  const pipScore = toPipScore(rgba, width, height);
  return detectWithMaps(tileMap, pipScore, options);
}
